import { IUploadPopUpRef } from "../UploadPopUp";
import styles from "./styles.module.css";
import {
  ToolType,
  list,
  getTitleFromToolType,
} from "../../../../utils/toolsConstants";
import { useRouter } from "next/router";
import BackgroundColorPopUp, {
  IBackgroundColorPopUpRef,
} from "../BackgroundColorPopUp";
import GoogleDriveUploadPopUp from "../GoogleDrivePopUp";
import { useEffect, useRef, useState } from "react";
import { useSetAtom, useAtomValue, useAtom } from "jotai";
import {
  segmentEventPayload,
  homepageFeatureAtom,
} from "@/modules/editor-v2/Atoms";
import { useSelector } from "react-redux";
import { TrackWebappEvent } from "src/modules/segment-event-tracking/segmentEventTracking";
import { EventTrackName } from "src/modules/segment-event-tracking/constant";
import { Skeleton } from "@mui/material";
import MarketplaceListing from "../MarketplaceListing";
import ImageResizerPopUp, { IImageResizerPopUpRef } from "../ImageResizerPopUp";
import { useSearchParams } from "next/navigation";
import { openFileUploaderAtom } from "@/modules/marketplaceListing/Atoms";
import { ICONS } from "@/modules/ui-elements/Icon";
import { getHomePageMarketPlaces } from "@/modules/editor-v2/services/HomePage";
import { useQuery } from "@tanstack/react-query";

export default function Tools({
  popUpRef,
}: {
  popUpRef: React.MutableRefObject<IUploadPopUpRef>;
}) {
  const getSegmentPayloadAtom = useAtomValue(segmentEventPayload);
  const setHomepageFeature = useSetAtom(homepageFeatureAtom);

  const router = useRouter();
  const backgroundColorPopUpRef = useRef<IBackgroundColorPopUpRef>();
  const imageResizerPopUpRef = useRef<IImageResizerPopUpRef>();
  const googleDrivePopUpRef = useRef<any>();
  const strapiData: any = useSelector<any>(
    (state) =>
      state.strapi?.entireStrapiData?.WebappHomepageV22?.WebappHomepageV22
  );
  const toolsServicesList: any = useSelector<any>(
    (state) =>
      state.strapi?.entireStrapiData?.ToolsTagOnHomepage?.ToolsTagOnHomepage
  );
  const [selectedTool, setSelectedTool] = useState<number>();
  const setOpenFileUploader = useSetAtom(openFileUploaderAtom);

  const searchParams = useSearchParams();
  const toolType = searchParams.get("toolType");

  useEffect(() => {
    const title = getTitleFromToolType(toolType);
    if (Object.keys(getSegmentPayloadAtom || {}).length && toolType) {
      switch (toolType) {
        case "backgroundRemoval":
          onClickGenerator(0, title)();
          break;

        case "backgroundColor":
          onClickGenerator(1, title)();
          break;

        case "imageResizer":
          onClickGenerator(2, title)();
          break;

        case "listingGenerator":
          onClickGenerator(3, title)();
          break;

        default:
          break;
      }
    }
  }, [searchParams, getSegmentPayloadAtom]);

  const toolsList = !toolsServicesList
    ? list
    : toolsServicesList
        ?.filter((tool) => tool?.Title)
        ?.map((item, index) => ({
          ...item,
          Image: item?.Image || list[index]?.image,
        }));

  const toolTrigger = (homepageFeature) => {
    setHomepageFeature(homepageFeature);
    const eventPayload = {
      homepageFeature: homepageFeature,
      ...getSegmentPayloadAtom,
    };
    TrackWebappEvent(
      `${process.env.NEXT_PUBLIC_EVENT_TRACK_TYPE}${EventTrackName._select_tool_hp}`,
      eventPayload
    );
  };

  const onUploadFailure =
    () =>
    async ({ error }) => {
      // popUpRef.current.close()
    };

  const onUploadSuccessGenerator = (toolType: number, title?: string) =>
    async ({ batchId="", initiatedSource, jobId="" }) => {
      if (Object.values(ToolType)[toolType] === ToolType.backgroundColor) {
        backgroundColorPopUpRef.current.open({ batchId,jobId });
        window.setTimeout(() => popUpRef.current.close(), 100); // This is to avoid flicker and due to timed nature of animations.
        return;
      }
      if (Object.values(ToolType)[toolType] === ToolType.imageResizer) {
        imageResizerPopUpRef.current.open({ batchId,jobId });
        window.setTimeout(() => popUpRef.current.close(), 100); // This is to avoid flicker and due to timed nature of animations.
        return;
      }
      if (Object.values(ToolType)[toolType] === ToolType.backgroundRemoval) {
        const presetDetails = {
          size: {
            height: 1500,
            width: 1500,
          },
          coverage: 80,
          position: "centre",
          backgroundColor: "#00000000",
        };
        jobId ? router.push(`/dashboard/batch-editor/${jobId}?&initiatedSource=${
            initiatedSource ? initiatedSource : false
          }&presetDetails=${encodeURIComponent(JSON.stringify(presetDetails))}`) 
        : router.push(
          `/dashboard/batch-editor?batchMode=multiple&batchJobId=${batchId}&initiatedSource=${
            initiatedSource ? initiatedSource : false
          }&presetDetails=${encodeURIComponent(JSON.stringify(presetDetails))}`
        );
        popUpRef.current.close();
        return;
      }
      jobId ? router.push(`/dashboard/batch-editor/${jobId}?&initiatedSource=${
          initiatedSource ? initiatedSource : false
        }`) 
      : router.push(
        `/dashboard/batch-editor?batchMode=multiple&batchJobId=${batchId}&initiatedSource=${
            initiatedSource ? initiatedSource : false
          }`
      );
      popUpRef.current.close();
    };

  const onSingleImageUploadSuccessGenerator = () => async (jobData: Object) => {
    if (!jobData || Object.keys(jobData).length === 0) return;
    const jobId = jobData?.job_id;
    router.push("/dashboard/editor/" + jobId + "?AI-backgrounds");
    popUpRef.current.close();
    return;
  };

  const onClickGenerator =
    (toolType: number, title: string, WidgetImage?: string) => () => {
      if (toolType === 3) {
        toolTrigger('Listing Generator');
        setOpenFileUploader(true);
        return;
      }
      // const singleImageBulkUpload = Object.values(ToolType)[toolType] === ToolType.aiBackgrounds
      toolType = toolType == undefined ? 0 : toolType;
      setSelectedTool(toolType);
      toolTrigger(title);
      const newQuery = { ...router.query };
      delete newQuery?.toolType;
      router.replace(
        { pathname: router.pathname, query: newQuery },
        undefined,
        { shallow: true }
      );
      popUpRef.current.open({
        title,
        // singleImageBulkUpload: singleImageBulkUpload,
        onUploadSuccess: onUploadSuccessGenerator(toolType),
        widgetImage: WidgetImage,
        batchEditorJobPopUp: true,
        onUploadFailure: onUploadFailure(),
        onGoogleDriveUpload: onGoogleDriveUploadGenerator(toolType),
        // onSingleImageBulkUpload: onSingleImageUploadSuccessGenerator(),
      });
    };

  const onGoogleDriveUploadGenerator = (toolType) => async () => {
    googleDrivePopUpRef?.current?.open({
      onUploadSuccess: onUploadSuccessGenerator(toolType),
    });
    window.setTimeout(() => popUpRef.current.close(), 100);
  };

  const onBackgroundColorPopUpBackPress = () => {
    popUpRef.current.open({
      title: "Background Color",
      onUploadSuccess: onUploadSuccessGenerator(ToolType.backgroundColor),
      widgetImage: toolsList?.find((item) => item.Title === "Background Color")
        ?.WidgetImage,
      batchEditorJobPopUp: true,
    });
    backgroundColorPopUpRef.current.close();
  };

  const onImageResizerBackPress = () => {
    popUpRef.current.open({
      title: "Image Resizer",
      onUploadSuccess: onUploadSuccessGenerator(ToolType.imageResizer),
      widgetImage: toolsList?.find((item) => item.Title === "Image Resizer")
        ?.WidgetImage,
      batchEditorJobPopUp: true,
    });
    imageResizerPopUpRef.current.close();
  };

  return (
    <div className={styles.container}>
      <div className={styles.heading}>
        {strapiData?.ToolsSectionHeading || "Enhance Images with AI Tools"}
      </div>
      <div className={styles.toolsContainer}>
        <div className={styles.listContainer}>
          {!toolsList ? (
            <>
              {Array(4)
                .fill(0)
                ?.map((_) => (
                  <Skeleton
                    animation={"wave"}
                    variant={"rectangular"}
                    width={216}
                    height={148}
                    style={{ marginRight: 6, borderRadius: 0 }}
                  />
                ))}
            </>
          ) : (
            toolsList.map((item, index) => (
              <div
                className={styles.listItem}
                onClick={onClickGenerator(
                  index,
                  item?.Title || item?.title,
                  item.WidgetImage
                )}
              >
                <img
                  src={item.Image ? item.Image : item.image}
                  className={styles.listImage}
                />
                <div className={styles.listItemTitleContainer}>
                  <div className={`${styles.listItemTitle} user-selection-disable`}>
                    <span>{item.Title ? item.Title : item.title}</span>
                    <img src={ICONS.rightArrowVector} style={{ color: '#FFFFFF !important' }} alt="" height={11} />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        <MarketplaceListing />
      </div>
      <GoogleDriveUploadPopUp
        ref={googleDrivePopUpRef}
        onUploadSuccess={onUploadSuccessGenerator(selectedTool)}
        onBackPress={onBackgroundColorPopUpBackPress}
        initiatedSource={Object.values(ToolType)[selectedTool]}
      />
      <BackgroundColorPopUp
        ref={backgroundColorPopUpRef}
        onBackPress={onBackgroundColorPopUpBackPress}
        toolType={selectedTool}
      />
      <ImageResizerPopUp
        ref={imageResizerPopUpRef}
        onBackPress={onImageResizerBackPress}
        toolType={selectedTool}
      />
    </div>
  );
}
